import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

/**
 * Avatar palette ported from the Verba reference mockups (colorMap in
 * src/lib/conference/data.ts). Neutral product hues only.
 */
export const avatarColors = {
  emerald: {
    bg: 'bg-emerald-500',
    text: 'text-emerald-300',
    ring: 'ring-emerald-500/40',
    dot: 'bg-emerald-400',
  },
  rose: { bg: 'bg-rose-500', text: 'text-rose-300', ring: 'ring-rose-500/40', dot: 'bg-rose-400' },
  violet: {
    bg: 'bg-violet-500',
    text: 'text-violet-300',
    ring: 'ring-violet-500/40',
    dot: 'bg-violet-400',
  },
  sky: { bg: 'bg-sky-500', text: 'text-sky-300', ring: 'ring-sky-500/40', dot: 'bg-sky-400' },
  amber: {
    bg: 'bg-amber-500',
    text: 'text-amber-300',
    ring: 'ring-amber-500/40',
    dot: 'bg-amber-400',
  },
  lime: { bg: 'bg-lime-500', text: 'text-lime-300', ring: 'ring-lime-500/40', dot: 'bg-lime-400' },
  fuchsia: {
    bg: 'bg-fuchsia-500',
    text: 'text-fuchsia-300',
    ring: 'ring-fuchsia-500/40',
    dot: 'bg-fuchsia-400',
  },
  teal: { bg: 'bg-teal-500', text: 'text-teal-300', ring: 'ring-teal-500/40', dot: 'bg-teal-400' },
  zinc: { bg: 'bg-zinc-500', text: 'text-zinc-300', ring: 'ring-zinc-500/40', dot: 'bg-zinc-400' },
} as const;

export type AvatarColorKey = keyof typeof avatarColors;

const avatarColorKeys = Object.keys(avatarColors) as AvatarColorKey[];

/** FNV-1a over the UTF-16 code units of the normalized name. */
function nameHash(name: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < name.length; i += 1) {
    hash ^= name.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** Deterministic avatar color for a display name: the same name always maps to the same hue. */
export function colorForName(name: string): AvatarColorKey {
  const key = avatarColorKeys[nameHash(name.trim().toLowerCase()) % avatarColorKeys.length];
  // The guard is unreachable by construction (index is taken modulo the array
  // length); strict TS still asks for it on array member access.
  return key ?? 'emerald';
}

/** First letters of the first two words, uppercased ('Михаил Орлов' -> 'МО'). */
export function initialsForName(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  const first = words[0]?.charAt(0) ?? '';
  const second = words.length > 1 ? (words[1]?.charAt(0) ?? '') : '';
  return (first + second).toUpperCase();
}

const sizeMap = {
  sm: 'h-7 w-7 text-[11px]',
  md: 'h-9 w-9 text-xs',
  lg: 'h-11 w-11 text-sm',
  xl: 'h-16 w-16 text-lg',
} as const;

export type AvatarSize = keyof typeof sizeMap;

interface UserAvatarProps {
  /** Display name: initials (and the hue, unless `color` is given) derive from it. */
  name: string;
  size?: AvatarSize;
  /** Accent ring around the avatar (speaking state in the Verba mockups). */
  ring?: boolean;
  /** Explicit palette key override; defaults to the deterministic colorForName(name). */
  color?: AvatarColorKey;
  className?: string;
}

/**
 * Shared avatar atom from the Verba design system: colored fallback circle with
 * name initials, fixed sizes and an optional state ring.
 */
export function UserAvatar({ name, size = 'md', ring = false, color, className }: UserAvatarProps) {
  const palette = avatarColors[color ?? colorForName(name)];
  return (
    <Avatar
      className={cn(
        sizeMap[size],
        palette.bg,
        'border-2 border-zinc-900 font-semibold text-white shadow-lg shadow-black/20',
        ring && 'ring-2',
        ring && palette.ring,
        className,
      )}
    >
      <AvatarFallback className={cn(palette.bg, 'font-semibold text-white')}>
        {initialsForName(name)}
      </AvatarFallback>
    </Avatar>
  );
}
